import * as XLSX from 'xlsx'
import { ExportButton } from '../../../../theme/Theme.Button'
import Excel from '../../../../icons/Excel'

interface HierarchicalData {
  subRows?: HierarchicalData[]
  [key: string]: any
}

interface ExcelExportConfig<T extends HierarchicalData> {
  data: T[]
  filename: string
  columns: {
    key: keyof T | string
    header: string
    width?: number
  }[]
  sheetName?: string
}

function formatCellValue(value: any): any {
  if (value === null || value === undefined) {
    return ''
  }
  if (typeof value === 'number') {
    return value
  }
  return String(value)
}

export function exportToExcel<T extends HierarchicalData>(config: ExcelExportConfig<T>): void {
  const { data, filename, columns, sheetName = 'Sheet1' } = config

  const worksheetData: any[][] = []

  // Add headers
  const headers = columns.map((col) => col.header)
  worksheetData.push(headers)

  // Add data rows
  for (const item of data) {
    const row = columns.map((col) => {
      let value: any = item[col.key as keyof typeof item]
      value = formatCellValue(value)
      return value
    })
    worksheetData.push(row)
  }

  const workbook = XLSX.utils.book_new()
  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData)

  const columnWidths = columns.filter((col) => col.width).map((col) => ({ wch: col.width || 10 }))

  if (columnWidths.length > 0) {
    worksheet['!cols'] = columnWidths
  }

  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName)

  XLSX.writeFile(workbook, filename)
}

interface ExcelExportButtonProps<T extends HierarchicalData> {
  data: T[]
  config: Omit<ExcelExportConfig<T>, 'data'>
}

export function TableExcelExportButton<T extends HierarchicalData>({ data, config }: ExcelExportButtonProps<T>) {
  const handleExport = () => {
    exportToExcel({ ...config, data })
  }

  return (
    <ExportButton as="button" onClick={handleExport}>
      <div className="flex flex-row justify-between">
        <div className="flex flex-col justify-center items-start px-1">
          <Excel />
        </div>
        <div className="flex flex-col justify-center items-start px-1">Eksporter</div>
      </div>
    </ExportButton>
  )
}

export function ExcelExportButton<T extends HierarchicalData>({
  label = 'Eksporter',
  className,
  data,
  config,
}: ExcelExportButtonProps<T> & { className?: string; label?: string }) {
  const handleExport = () => {
    exportToExcel({ ...config, data })
  }

  return (
    <button
      type="button"
      className={`${className} w-fit gap-2 bg-blueDark text-interaction hover:text-interactionHover transition-all ease-in-out flex items-center px-3 py-2 rounded-sm `}
      onClick={handleExport}
    >
      <span className="scale-75">
        <Excel />
      </span>
      <span>{label}</span>
    </button>
  )
}
